import { type PostgrestError } from '@supabase/postgrest-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  FEATURE_MATCH_COLUMN_NAME,
  FeatureMatchStatus,
  getFeatureMatchDisplayValue,
} from '@/components/geomatching';
import { BoundaryInfo, DataSourceConfig } from '@/components/maplibre/types';
import { geoforgeStore } from '@/components/providers/geoforge-store-provider';
import { Database, Json } from '@/lib/database.types';
import { LAYER_GEOMETRY_TYPE, LAYER_STATUS, UserCreatedLayerTypes } from '@/lib/db-constants';
import {
  Layer,
  LayerColumnCore,
  LayerFolder,
  type Map,
  MapLayer,
  UserProfile,
} from '@/lib/db-row-types';
import { LayerTable } from '@/lib/stores/slices/layerTables.slice';
import { pickDistinctLayerColors } from '@/utils/colors';
import {
  buildStatsDisplayNameMap,
  resolveColumnDisplayName,
} from '@/utils/layer-sources/column-display-names';
import { buildLayerDataSources } from '@/utils/layer-sources/layer-fetcher';
import { STYLE_MODE } from '@/utils/layer-sources/style-constants';
import { getDefaultStyleV2ForLayer } from '@/utils/layer-sources/style-defaults';
import type { StyleV2 } from '@/utils/layer-sources/style-v2';
import { RequestOptions, defaultRequestOptions } from '@/utils/request-helpers';
import { getBrowserClient } from '@/utils/supabase/client';
import { getAccessToken } from '@/utils/supabase/token-manager';
import { handleEntityDeleteWithConfirm } from './utils';

// Types for combined folders and layers
export type FolderWithMeta = Omit<LayerFolder, 'name'> & {
  title: string;
  itemType: 'folder';
  layers: Array<{ id: string; title: string }>;
  updated_by_user?: Pick<UserProfile, 'id' | 'display_name'> | null;
};

export type LayerWithMeta = Layer & {
  itemType: 'layer';
  maps: { id: string; title: string }[];
  updated_by_user?: Pick<UserProfile, 'id' | 'display_name'> | null;
};

export type CombinedLayerItem = FolderWithMeta | LayerWithMeta;

export type UpdateLayerStatusArgs = {
  layerId: string;
  status: Database['public']['Enums']['layer_status'];
};

export type UpdateLayerGeometryTypeArgs = {
  layerId: string;
  geometryType: Database['public']['Enums']['layer_geometry_type'];
};

interface AddLayerColumnArgs {
  layerId: string;
  name: string;
  isReadOnly?: boolean;
  position?: number;
}

/**
 * Deletes a layer by its id, including GCS cleanup via geoforge-worker.
 * @param id The id of the layer to delete.
 * @param workspaceId The workspace the layer belongs to.
 * @returns The result of the delete operation.
 */
export async function deleteLayerById(id: string, workspaceId: string) {
  try {
    const accessToken = await getAccessToken();
    const serviceUrl = process.env.NEXT_PUBLIC_GEOFORGE_WORKER_URL;

    if (!serviceUrl || !accessToken) {
      return { data: null, error: new Error('Service configuration error') };
    }

    const response = await fetch(`${serviceUrl}/api/workspaces/${workspaceId}/layers/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`),
      };
    }

    return { data: null, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Network error'),
    };
  }
}

/**
 * Best-effort cleanup of agent data for a layer after it is unlinked from a map.
 * @param id The id of the layer.
 * @param workspaceId The workspace the layer belongs to.
 * @returns The result of the cleanup request.
 */
export async function deleteLayerAgentData(id: string, workspaceId: string) {
  try {
    const accessToken = await getAccessToken();
    const serviceUrl = process.env.NEXT_PUBLIC_GEOFORGE_WORKER_URL;

    if (!serviceUrl || !accessToken) {
      return { data: null, error: new Error('Service configuration error') };
    }

    const response = await fetch(
      `${serviceUrl}/api/workspaces/${workspaceId}/layers/${id}/agent-data`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`),
      };
    }

    return { data: null, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Network error'),
    };
  }
}

/**
 * Fetches all layers from the 'layers' table for a specific workspace.
 */
export async function fetchAllLayers(activeWorkspaceId: string) {
  const store = geoforgeStore.getState();
  store.setLayersLoading(true);

  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layers')
    .select(
      `
      *,
      maps:map_layers(
        maps(id, title)
      )
    `
    )
    .eq('workspace_id', activeWorkspaceId)
    .eq('is_internal', false)
    .neq('type', 'site');

  if (!error && data) {
    const flattenedData = data.map(layer => ({
      ...layer,
      maps: layer.maps?.flatMap(mapLayer => mapLayer.maps) || [],
    }));

    store.setLayers(flattenedData);
    return { data: flattenedData, error };
  } else {
    store.setLayersLoading(false);
  }

  return { data, error };
}

/**
 * Fetch layers available to attach in the current workspace context.
 * Includes:
 *  - layers where workspace_id equals the active workspace
 *  - common layers where workspace_id IS NULL (currently parcels-only per spec)
 */
export async function fetchAttachableLayers(activeWorkspaceId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layers')
    .select('*')
    .or(`workspace_id.eq.${activeWorkspaceId},workspace_id.is.null`)
    .eq('is_internal', false)
    .neq('type', 'site');
  return { data, error };
}

/**
 * Fetches layers with pagination support for infinite scrolling.
 * @param activeWorkspaceId The workspace ID to fetch layers for.
 * @param limit The number of layers to fetch per page.
 * @param offset The offset for pagination.
 * @returns Paginated layers data.
 */

export async function fetchLayersPaginated(
  activeWorkspaceId: string,
  options: RequestOptions<Layer>
) {
  const { limit, offset, sorting } = options.pagination ?? defaultRequestOptions.pagination!;
  const { query } = options;

  const supabase = getBrowserClient();
  const request = supabase
    .from('layers')
    .select(
      `
      *,
      maps:map_layers(
        maps(id, title)
      ),
      updated_by_user:user_profiles!updated_by(
        id,
        display_name
      )
    `,
      { count: 'exact' }
    )
    .eq('workspace_id', activeWorkspaceId)
    .eq('is_internal', false)
    .neq('type', 'site')
    .order(sorting.id, { ascending: !sorting.desc })
    .range(offset, offset + limit - 1);

  if (query && query.value !== '') {
    request.ilike(query.column, `%${query.value}%`);
  }

  const { data, error, count } = await request;

  const flattenedData = data?.map(layer => ({
    ...layer,
    maps: layer.maps?.flatMap(mapLayer => mapLayer.maps) || [],
  }));

  return { data: flattenedData || [], error, count };
}

/**
 * Helper: Calculates pagination offsets for folders and layers
 */
function calculatePaginationOffsets(params: {
  offset: number;
  limit: number;
  totalFolders: number;
  totalLayers: number;
}) {
  const { offset, limit, totalFolders, totalLayers } = params;
  let foldersToFetch = 0;
  let layersToFetch = 0;
  let foldersOffset = 0;
  let layersOffset = 0;

  if (offset < totalFolders) {
    // We're in the folders section or spanning both
    foldersOffset = offset;
    foldersToFetch = Math.min(limit, totalFolders - offset);

    // If we have room for layers after folders
    const remainingSpace = limit - foldersToFetch;
    if (remainingSpace > 0) {
      layersToFetch = Math.min(remainingSpace, totalLayers);
      layersOffset = 0;
    }
  } else {
    // We're past all folders, only fetch layers
    layersOffset = offset - totalFolders;
    layersToFetch = Math.min(limit, totalLayers - layersOffset);
  }

  return { foldersToFetch, layersToFetch, foldersOffset, layersOffset };
}

/**
 * Helper: Fetches folders with pagination and search
 */
async function fetchFolders(params: {
  supabase: SupabaseClient;
  activeWorkspaceId: string;
  foldersToFetch: number;
  foldersOffset: number;
  folderSortColumn: string;
  sortDesc: boolean;
  searchQuery?: { column: string; value: string };
}) {
  const {
    supabase,
    activeWorkspaceId,
    foldersToFetch,
    foldersOffset,
    folderSortColumn,
    sortDesc,
    searchQuery,
  } = params;

  if (foldersToFetch === 0) {
    return { data: [], error: null };
  }

  let foldersQuery = supabase
    .from('layer_folders')
    .select(
      `
      *,
      updated_by_user:user_profiles!updated_by(
        id,
        display_name
      ),
      layers(id, title)
    `
    )
    .eq('workspace_id', activeWorkspaceId)
    .order(folderSortColumn, { ascending: !sortDesc })
    .range(foldersOffset, foldersOffset + foldersToFetch - 1);

  if (searchQuery && searchQuery.value !== '') {
    foldersQuery = foldersQuery.ilike('name', `%${searchQuery.value}%`);
  }

  return await foldersQuery;
}

/**
 * Helper: Fetches layers with pagination and search
 */
async function fetchLayers(params: {
  supabase: SupabaseClient;
  activeWorkspaceId: string;
  layersToFetch: number;
  layersOffset: number;
  sortId: keyof Layer;
  sortDesc: boolean;
  searchQuery?: { column: string; value: string };
}) {
  const {
    supabase,
    activeWorkspaceId,
    layersToFetch,
    layersOffset,
    sortId,
    sortDesc,
    searchQuery,
  } = params;

  if (layersToFetch === 0) {
    return { data: [], error: null };
  }

  let layersQuery = supabase
    .from('layers')
    .select(
      `
      *,
      maps:map_layers(
        maps(id, title)
      ),
      updated_by_user:user_profiles!updated_by(
        id,
        display_name
      )
    `
    )
    .eq('workspace_id', activeWorkspaceId)
    .eq('is_internal', false)
    .neq('type', 'site')
    .is('folder_id', null)
    .order(sortId, { ascending: !sortDesc })
    .range(layersOffset, layersOffset + layersToFetch - 1);

  if (searchQuery && searchQuery.value !== '') {
    layersQuery = layersQuery.ilike('title', `%${searchQuery.value}%`);
  }

  return await layersQuery;
}

/**
 * Helper: Combines folders and layers into a single array
 */
function combineResults(
  folders: Array<
    LayerFolder & {
      updated_by_user?: Pick<UserProfile, 'id' | 'display_name'> | null;
      layers?: Array<{ id: string; title: string }>;
    }
  >,
  layers: Array<
    Layer & {
      maps?: Array<{ maps: { id: string; title: string } | null }>;
      updated_by_user?: Pick<UserProfile, 'id' | 'display_name'> | null;
    }
  >
): CombinedLayerItem[] {
  const foldersWithMeta: FolderWithMeta[] = folders.map(folder => {
    const { name, layers: folderLayers, ...rest } = folder;
    return {
      title: name,
      ...rest,
      itemType: 'folder' as const,
      layers: folderLayers || [],
    } as FolderWithMeta;
  });

  const layersWithMeta: LayerWithMeta[] = layers.map(layer => {
    return {
      ...layer,
      itemType: 'layer' as const,
      maps:
        (layer.maps?.flatMap(mapLayer => mapLayer.maps).filter(Boolean) as Array<{
          id: string;
          title: string;
        }>) || [],
    } as LayerWithMeta;
  });

  return [...foldersWithMeta, ...layersWithMeta];
}

/**
 * Fetches both folders and layers with pagination support.
 * Folders are always shown first, sorted separately from layers.
 * Applies pagination at the database query level for efficiency.
 * @param activeWorkspaceId The workspace ID to fetch data for.
 * @param options Request options including pagination, sorting, and search.
 * @param withFolders Whether to include folders in the results. Defaults to true.
 * @param counts Optional counts object. Defaults to 30 for both if not provided.
 * @returns Combined folders and layers data with pagination.
 */
export async function fetchLayersAndFoldersPaginated(
  activeWorkspaceId: string,
  options: RequestOptions<Layer>,
  withFolders: boolean = true,
  counts?: { foldersCount: number; layersCount: number }
) {
  const { limit, offset, sorting } = options.pagination ?? defaultRequestOptions.pagination!;
  const { query } = options;
  const supabase = getBrowserClient();

  if (!withFolders) {
    const result = await fetchLayersPaginated(activeWorkspaceId, options);
    return {
      ...result,
      data: result.data.map(layer => ({
        ...layer,
        itemType: 'layer' as const,
      })) as LayerWithMeta[],
      foldersCount: 0,
      layersCount: result.count || 0,
    };
  }

  const totalFolders = counts?.foldersCount ?? 0;
  const totalLayers = counts?.layersCount ?? 0;

  const totalCount = totalFolders + totalLayers;

  const { foldersToFetch, layersToFetch, foldersOffset, layersOffset } = calculatePaginationOffsets(
    {
      offset,
      limit,
      totalFolders,
      totalLayers,
    }
  );

  const folderSortColumn = sorting.id === 'title' ? 'name' : sorting.id;

  const [foldersResult, layersResult] = await Promise.all([
    fetchFolders({
      supabase,
      activeWorkspaceId,
      foldersToFetch,
      foldersOffset,
      folderSortColumn,
      sortDesc: sorting.desc,
      searchQuery: query || undefined,
    }),
    fetchLayers({
      supabase,
      activeWorkspaceId,
      layersToFetch,
      layersOffset,
      sortId: sorting.id,
      sortDesc: sorting.desc,
      searchQuery: query || undefined,
    }),
  ]);

  if (foldersResult.error || layersResult.error) {
    return {
      data: [],
      error: foldersResult.error || layersResult.error,
      count: totalCount,
      foldersCount: totalFolders,
      layersCount: totalLayers,
    };
  }

  const folders = foldersResult.data || [];
  const layers = layersResult.data || [];

  const combinedData = combineResults(folders, layers);

  return {
    data: combinedData,
    error: null,
    count: totalCount,
    foldersCount: totalFolders,
    layersCount: totalLayers,
  };
}

/**
 * Fetches common layers (workspace_id IS NULL) with pagination support.
 */
export async function fetchCommonLayersPaginated(options: RequestOptions<Layer>) {
  const { limit, offset, sorting } = options.pagination ?? defaultRequestOptions.pagination!;
  const { query } = options;

  const supabase = getBrowserClient();
  const request = supabase
    .from('layers')
    .select(
      `
      *,
      maps:map_layers(
        maps(id, title)
      ),
      updated_by_user:user_profiles!updated_by(
        id,
        display_name
      )
    `,
      { count: 'exact' }
    )
    .is('workspace_id', null)
    .neq('type', 'site')
    .order(sorting.id, { ascending: !sorting.desc })
    .range(offset, offset + limit - 1);

  if (query && query.value !== '') {
    request.ilike(query.column, `%${query.value}%`);
  }

  const { data, error, count } = await request;

  const flattenedData = data?.map(layer => ({
    ...layer,
    maps: layer.maps?.flatMap(mapLayer => mapLayer.maps) || [],
  }));

  return { data: flattenedData || [], error, count };
}

/**
 * Fetches a layer by id from the 'layers' table.
 */
export async function fetchLayersTableDataById(id: string) {
  const supabase = getBrowserClient();
  // Fetch the layer metadata
  const { data: layerMeta, error: layerError } = await supabase
    .from('layers')
    .select('*')
    .eq('id', id)
    .single();
  if (layerError || !layerMeta) return { data: null, error: layerError || 'Layer not found' };

  // Fetch all columns for the layer from layer_columns
  const { data: columns, error: columnsError } = await supabase
    .from('layer_columns')
    .select('id, column_name, display_name, position, is_read_only')
    .eq('layer_id', id);
  if (columnsError) return { data: null, error: columnsError };

  // layer_columns.display_name is null for tiled uploads; the original label
  // lives on layer.stats.fields. Resolve it so data-table headers show the
  // human-readable column name (GEO-589).
  const statsDisplayNames = buildStatsDisplayNameMap(layerMeta.stats);
  const enrichedColumns = (columns ?? []).map(column => ({
    ...column,
    display_name: resolveColumnDisplayName(
      column.column_name,
      column.display_name,
      statsDisplayNames
    ),
  }));

  /// TODO: Handle pagination as supabase will only return 1000 rows max
  const { data: tableRows, error: tableError } = await supabase
    .rpc('get_raw_layer_data', {
      layer_id: id,
    })
    .select('row_id, properties, feature_id, feature_match');

  const isParcelCollection = layerMeta.type === 'parcel';

  const ret = {
    data: {
      layer: layerMeta,
      table: convertToLayerTable(enrichedColumns, tableRows || [], isParcelCollection),
    },
    error: tableError,
  };

  return ret;
}

export async function fetchAndSetLayerTableData(
  layerId: string,
  addLayer: (layer: Layer) => void,
  setLayerTable: (layerId: string, table: LayerTable) => void
) {
  const { data, error } = await fetchLayersTableDataById(layerId);
  if (data) {
    addLayer(data.layer);
    setLayerTable(layerId, data.table);
  }
  return { data, error };
}

/**
 * Fetches all map_layers from the 'map_layers' table.
 */
export async function fetchAllMapLayers() {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.from('map_layers').select('*');
  if (!error && data) {
    geoforgeStore.getState().setMapLayers(data);
  }
  return { data, error };
}

/**
 * Fetches all map_layers for a given map_id from the 'map_layers' table.
 */
export async function fetchMapLayersByMapId(mapId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.from('map_layers').select('*').eq('map_id', mapId);
  return { data, error };
}

export async function fetchMapLayersGroupsByMapId(mapId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('map_layers_groups')
    .select(
      `
      *,
      map_layers!group_id(
        *,
        layers(*)
      )
    `
    )
    .eq('map_id', mapId)
    .order('created_at', { ascending: true });
  return { data, error };
}

/**
 * Fetches layers by an array of ids from the 'layers' table.
 */
export async function fetchLayersByIds(ids: string[]) {
  if (!ids.length) return { data: [], error: null };
  const supabase = getBrowserClient();
  const { data, error } = await supabase.from('layers').select('*').in('id', ids);
  return { data, error };
}

/**
 * Fetches layer by an id from the 'layers' table.
 */
export async function fetchLayerById(id: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.from('layers').select('*').eq('id', id).single();
  return { data, error };
}

/**
 * Optimized function that fetches map_layers, layers, and map_layers_groups
 * for a specific map in a single query with nested JOINs.
 */
export async function fetchMapLayersWithData(mapId: string): Promise<{
  mapLayers: MapLayer[];
  layers: Layer[];
  dataSources: DataSourceConfig[];
  groups: NonNullable<Awaited<ReturnType<typeof fetchMapLayersGroupsByMapId>>['data']>;
  error: any;
}> {
  const supabase = getBrowserClient();

  const { data: mapData, error } = await supabase
    .from('maps')
    .select(
      `
      id,
      map_layers (
        *,
        layers (
          id,
          title,
          description,
          type,
          workspace_id,
          status,
          boundary_id,
          created_at,
          updated_at,
          created_by,
          updated_by,
          stats,
          folder_id,
          current_revision_id,
          geometry_type,
          max_zoom,
          min_zoom,
          bbox,
          source_filename,
          tileset_format,
          raster_algorithm,
          updated_by_user:user_profiles!updated_by(
            id,
            display_name
          )
        )
      ),
      map_layers_groups (
        *,
        map_layers!group_id (
          *,
          layers(*)
        )
      )
    `
    )
    .eq('id', mapId)
    .single();

  if (error) {
    return { mapLayers: [], layers: [], dataSources: [], groups: [], error };
  }

  if (!mapData || !mapData.map_layers) {
    return {
      mapLayers: [],
      layers: [],
      dataSources: [],
      groups: mapData?.map_layers_groups || [],
      error: null,
    };
  }

  const mapLayers = mapData.map_layers.map(item => ({
    id: item.id,
    map_id: item.map_id,
    layer_id: item.layer_id,
    mapbox_style_data: item.mapbox_style_data,
    created_at: item.created_at,
    updated_at: item.updated_at,
    created_by: item.created_by,
    updated_by: item.updated_by,
    group_id: item.group_id,
  }));

  const layers = mapData.map_layers
    .map(item => item.layers)
    .filter(layer => layer !== null)
    .map(layer => layer as unknown as Layer);

  const dataSources = await buildLayerDataSources(layers, mapLayers);

  return {
    mapLayers,
    layers,
    dataSources,
    groups: mapData.map_layers_groups || [],
    error: null,
  };
}

/**
 * Fetches boundary information for a layer
 */
export async function fetchBoundaryInfo(boundaryId: string): Promise<BoundaryInfo | null> {
  try {
    const supabase = getBrowserClient();
    const { data: boundary, error } = await supabase
      .from('boundaries')
      .select('id, name, source, source_layer')
      .eq('id', boundaryId)
      .single();

    if (error || !boundary) {
      console.error('Failed to fetch boundary info for ID:', boundaryId, error);
      return null;
    }

    return boundary;
  } catch (error) {
    console.error('Error fetching boundary info:', error);
    return null;
  }
}

/**
 * Fetches all columns for a given layer_id from the 'layer_columns' table.
 */
export async function fetchLayerColumns(layerId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layer_columns')
    .select('*')
    .eq('layer_id', layerId)
    .order('position', { ascending: true });
  return { data, error };
}

/**
 * Fetches columns metadata for a given layerId from the 'layer_columns' table.
 */
export async function fetchLayerColumnsMetadata(layerId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layer_columns')
    .select('id, column_name, data_type, is_read_only')
    .eq('layer_id', layerId);
  return { data, error };
}

/**
 * Gets the layer_id for a given row_id from the layer_rows table.
 */
export async function getLayerIdFromRowId(rowId: number): Promise<string | null> {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layer_rows')
    .select('layer_id')
    .eq('id', rowId)
    .single();

  if (error) {
    // Row not found - this can happen during realtime events for deleted rows
    // This is expected behavior, so we don't log the error
    return null;
  }

  return data?.layer_id || null;
}

export function convertToLayerTable(
  columns: LayerColumnCore[],
  tableRows: Record<string, any>[],
  isParcelCollection: boolean = false
): LayerTable {
  const includeFeatureMatch = !isParcelCollection;
  const orderedColumns = [...columns].sort((a, b) => a.position - b.position);

  const featureMatchColumn: LayerColumnCore = {
    id: -1,
    is_read_only: false,
    column_name: FEATURE_MATCH_COLUMN_NAME,
    position: -1,
  };

  const allColumns = includeFeatureMatch ? [featureMatchColumn, ...orderedColumns] : orderedColumns;

  const data: {
    rowId: number;
    data: Json[];
    feature_match?: FeatureMatchStatus;
    featureId?: string;
  }[] = tableRows.map(row => {
    const props = row.properties;
    const featureMatch = row.feature_match || null;

    const dataArray = includeFeatureMatch
      ? [
          getFeatureMatchDisplayValue(featureMatch),
          ...orderedColumns.map(col =>
            props.hasOwnProperty(col.column_name) ? props[col.column_name] : null
          ),
        ]
      : orderedColumns.map(col =>
          props.hasOwnProperty(col.column_name) ? props[col.column_name] : null
        );

    return {
      rowId: row.row_id,
      data: dataArray,
      feature_match: featureMatch,
      featureId:
        typeof row.feature_id === 'string' || typeof row.feature_id === 'number'
          ? String(row.feature_id)
          : undefined,
    };
  });

  return {
    columns: allColumns,
    data,
  };
}

export function handleLayerDeleteWithConfirm({
  layer,
  removeLayer,
  addLayer,
  confirm,
}: {
  layer: Layer;
  removeLayer: (id: string) => void;
  addLayer: (layer: Layer) => void;
  confirm: () => Promise<boolean> | boolean;
}) {
  return handleEntityDeleteWithConfirm<Layer>({
    entity: layer,
    removeEntity: removeLayer,
    addEntity: addLayer,
    confirm,
    deleteFn: (id: string) => deleteLayerById(id, layer.workspace_id ?? ''),
  });
}

/**
 * Updates an existing layer in the 'layers' table.
 * @param id The id of the layer to update.
 * @param title The new title of the layer.
 * @param description The new description of the layer (optional).
 * @param workspaceId The workspace ID to validate the layer belongs to.
 * @returns The updated layer or an error.
 */
export async function updateLayer({
  id,
  title,
  description,
  workspaceId,
}: {
  id: string;
  title: string;
  description?: string | null;
  workspaceId: string;
}) {
  const supabase = getBrowserClient();
  const updateData = {
    title: title.trim(),
    description: description?.trim() || null,
  };
  const { data, error } = await supabase
    .from('layers')
    .update(updateData)
    .eq('id', id)
    .eq('workspace_id', workspaceId)
    .select()
    .single();

  if (error || !data) {
    return { error, data: null };
  }

  const updatedLayer: Layer = data;
  return { data: updatedLayer, error: null };
}

/**
 * Fetches the ReportAll parcels boundary ID dynamically
 */
async function getReportAllBoundaryId(): Promise<string | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('boundaries')
    .select('id')
    .eq('source_layer', 'parcels')
    .single();

  if (error || !data) {
    console.warn('Failed to fetch ReportAll boundary ID:', error);
    return null;
  }

  return data.id;
}

/**
 * Creates a new layer in the 'layers' table.
 * @param title The title of the layer.
 * @param description The description of the layer (optional).
 * @param workspaceId The workspace ID to assign the layer to.
 * @param type The type of layer (defaults to 'standard').
 * @returns The newly created layer or an error.
 */
export async function createLayer({
  title,
  description,
  workspaceId,
  type = 'standard',
  status = LAYER_STATUS.OK,
}: {
  title: string;
  description?: string | null;
  workspaceId: string;
  type?: UserCreatedLayerTypes;
  status?: Database['public']['Enums']['layer_status'];
}) {
  const supabase = getBrowserClient();
  const id = crypto.randomUUID();

  let boundaryId: string | null = null;
  if (type === 'parcel') {
    boundaryId = await getReportAllBoundaryId();
    if (!boundaryId) {
      return {
        error: { message: 'ReportAll boundary not found. Cannot create parcel layer.' },
        data: null,
      };
    }
  }

  const newLayerInsert: Pick<
    Layer,
    | 'id'
    | 'title'
    | 'description'
    | 'workspace_id'
    | 'type'
    | 'boundary_id'
    | 'status'
    | 'geometry_type'
  > = {
    id,
    title: title.trim(),
    description: description?.trim() || null,
    workspace_id: workspaceId,
    type,
    boundary_id: boundaryId,
    status,
    geometry_type: null,
  };

  if (type === 'parcel') {
    newLayerInsert.geometry_type = LAYER_GEOMETRY_TYPE.POLYGON;
  }

  const { data, error } = await supabase.from('layers').insert(newLayerInsert).select();
  if (error || !data || !data[0]) {
    return { error, data: null };
  }
  const newLayer: Layer = data[0];
  return { data: newLayer, error: null };
}

export async function createLayerGroup({ title, mapId }: { title: string; mapId: string }) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('map_layers_groups')
    .insert({
      title,
      map_id: mapId,
    })
    .select();
  if (error || !data || !data[0]) {
    return { error, data: null };
  }
  const newLayerGroup = data[0];
  return { data: newLayerGroup, error: null };
}

export async function renameLayerGroup({ id, title }: { id: string; title: string }) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('map_layers_groups')
    .update({
      title,
    })
    .eq('id', id)
    .select();

  if (error || !data || !data[0]) {
    return { error, data: null };
  }
  const newLayerGroup = data[0];
  return { data: newLayerGroup, error: null };
}

export async function deleteLayerGroup({ id }: { id: string }) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.from('map_layers_groups').delete().eq('id', id).select();

  if (error) {
    return { error, data: null };
  }
  return { data, error: null };
}

export async function addLayerToGroup({
  mapLayerId,
  groupId,
}: {
  mapLayerId: number;
  groupId: string;
}) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('map_layers')
    .update({ group_id: groupId })
    .eq('id', mapLayerId)
    .select();
  if (error || !data || !data[0]) {
    return { error, data: null };
  }
  return { data: data[0], error: null };
}

export async function addLayersToGroup({
  mapLayerIds,
  groupId,
}: {
  mapLayerIds: number[];
  groupId: string;
}) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('map_layers')
    .update({ group_id: groupId })
    .in('id', mapLayerIds)
    .select();
  if (error) {
    return { error, data: null };
  }
  return { data, error: null };
}

export async function removeLayerFromGroup({ mapLayerId }: { mapLayerId: number }) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('map_layers')
    .update({ group_id: null })
    .eq('id', mapLayerId)
    .select()
    .single();
  if (error || !data) {
    return { error, data: null };
  }
  return { data, error: null };
}

/**
 * Extracts the fixed polygon/line/point colors from a set of stored styles.
 * Data-driven (non-fixed) color modes are skipped — they don't visually
 * "occupy" a single color. Pure helper so both DB-query and store-read
 * callers share the same definition of "colors in use".
 *
 * Expects StyleV2-shaped inputs; v1 styles (raw MapLibre paint dicts) lack
 * `polygon.fill.color.mode` and will simply yield no colors, which is the
 * safe fallback for the color-picker use-case.
 */
export function extractFixedColorsFromStyles(
  styles: (Json | StyleV2 | null | undefined)[]
): string[] {
  const colors: string[] = [];
  for (const raw of styles) {
    if (!raw) continue;
    const style = raw as StyleV2;
    const polyFill = style.polygon?.fill?.color;
    if (polyFill?.mode === STYLE_MODE.FIXED) colors.push(polyFill.value);
    const line = style.line?.color;
    if (line?.mode === STYLE_MODE.FIXED) colors.push(line.value);
    const point = style.point?.color;
    if (point?.mode === STYLE_MODE.FIXED) colors.push(point.value);
  }
  return colors;
}

/**
 * Returns the fixed colors currently in use on a map's layers, so
 * newly-linked layers can pick something distinct.
 */
async function getExistingMapLayerColors(mapId: string): Promise<string[]> {
  const supabase = getBrowserClient();
  const { data } = await supabase
    .from('map_layers')
    .select('mapbox_style_data')
    .eq('map_id', mapId);
  if (!data) return [];
  return extractFixedColorsFromStyles(data.map(row => row.mapbox_style_data));
}

/**
 * Picks a distinct default style for a newly-inserted map_layer row that
 * arrived with no style (the worker RPC path). Pure — does not touch the
 * store or the DB. Returns undefined if the layer already has a style or
 * the helper should be a no-op for some other reason.
 */
export function computeDistinctStyleForNewLayer(
  newLayer: MapLayer,
  existingMapLayersOnMap: MapLayer[]
): StyleV2 | undefined {
  if (newLayer.mapbox_style_data) return undefined;

  const existingColors = extractFixedColorsFromStyles(
    existingMapLayersOnMap
      .filter(ml => ml.map_id === newLayer.map_id && ml.id !== newLayer.id)
      .map(ml => ml.mapbox_style_data)
  );
  const [color] = pickDistinctLayerColors(1, existingColors);
  return getDefaultStyleV2ForLayer(newLayer.layer_id, color);
}

/**
 * Persists a StyleV2 onto a map_layers row, but only if the row is still
 * NULL — concurrent clients racing to auto-assign will no-op after the first
 * writer wins. GEO-573.
 *
 * Logs (but does not throw) on real DB errors so the UI keeps its
 * optimistic render. The lost-race case (row no longer NULL) returns no
 * rows and no error, so it stays correctly silent.
 */
export async function persistStyleIfMapLayerNull(
  mapLayerId: number,
  style: StyleV2
): Promise<void> {
  const supabase = getBrowserClient();
  const { error } = await supabase
    .from('map_layers')
    .update({ mapbox_style_data: style as Json })
    .eq('id', mapLayerId)
    .is('mapbox_style_data', null);
  if (error) {
    console.warn(
      '[GEO-573] Failed to persist auto-assigned color for map_layer',
      mapLayerId,
      error
    );
  }
}

/**
 * Links a layer to a map by inserting into the 'map_layers' table.
 * Automatically sets appropriate default styling for layers, auto-assigning
 * a color distinct from the map's other layers (GEO-573).
 * @param mapId The id of the map.
 * @param layerId The id of the layer.
 * @returns The result of the Supabase insert operation.
 */
export async function linkLayerToMap(map: Map, layerId: string) {
  const supabase = getBrowserClient();

  const { data: layer } = await supabase
    .from('layers')
    .select('default_mapbox_style_data')
    .eq('id', layerId)
    .single();

  let styleConfig: Json;
  if (layer?.default_mapbox_style_data) {
    styleConfig = layer.default_mapbox_style_data;
  } else {
    const existingColors = await getExistingMapLayerColors(map.id);
    const [color] = pickDistinctLayerColors(1, existingColors);
    styleConfig = getDefaultStyleV2ForLayer(layerId, color) as Json;
  }

  return await supabase
    .from('map_layers')
    .insert({ map_id: map.id, layer_id: layerId, mapbox_style_data: styleConfig });
}

/**
 * Links multiple layers to a map by inserting into the 'map_layers' table.
 * Each new layer gets its own distinct color from the palette.
 *
 * The insert uses `.select()` so callers receive the inserted map_layer rows
 * back in `data` and can update local store state without waiting for the
 * Supabase realtime channel to deliver the INSERT.
 *
 * @param map The map.
 * @param layerIds The ids of the layers.
 * @returns The result of the Supabase insert operation.
 */
export async function linkLayersToMap(map: Map, layerIds: string[]) {
  const supabase = getBrowserClient();

  if (layerIds.length === 0) {
    return { data: [], error: null };
  }

  const { data: layers } = await supabase
    .from('layers')
    .select('id, default_mapbox_style_data')
    .in('id', layerIds);

  const defaultsByLayerId: Record<string, Json | null> = {};
  for (const l of layers ?? []) {
    defaultsByLayerId[l.id] = l.default_mapbox_style_data;
  }

  const idsNeedingColor = layerIds.filter(id => !defaultsByLayerId[id]);
  const existingColors = idsNeedingColor.length > 0 ? await getExistingMapLayerColors(map.id) : [];
  const colors = pickDistinctLayerColors(idsNeedingColor.length, existingColors);
  let colorIdx = 0;

  const mapLayers = layerIds.map(layerId => {
    const defaultStyle = defaultsByLayerId[layerId];
    const styleConfig: Json = defaultStyle
      ? defaultStyle
      : (getDefaultStyleV2ForLayer(layerId, colors[colorIdx++]) as Json);
    return { map_id: map.id, layer_id: layerId, mapbox_style_data: styleConfig };
  });

  return await supabase.from('map_layers').insert(mapLayers).select();
}

/**
 * Unlinks a layer from a map by deleting from the 'map_layers' table.
 * @param mapId The id of the map.
 * @param layerId The id of the layer.
 * @returns The result of the Supabase delete operation.
 */
export async function unlinkLayerFromMap(mapId: string, layerId: string) {
  const supabase = getBrowserClient();
  return await supabase.from('map_layers').delete().eq('map_id', mapId).eq('layer_id', layerId);
}

/**
 * Unlinks multiple layers from a map by deleting them from the 'map_layers' table.
 * @param mapId The id of the map.
 * @param layerIds The ids of the layers.
 * @returns The result of the Supabase delete operation.
 */
export async function unlinkLayersFromMap(map: Map, layerIds: string[]) {
  const supabase = getBrowserClient();
  if (layerIds.length === 0) {
    return { data: [], error: null };
  }
  return await supabase
    .from('map_layers')
    .delete()
    .eq('map_id', map.id)
    .in('layer_id', layerIds)
    .select();
}

/**
 * Fetches all existing column names for a given layer (optimized for batch operations)
 * @param layerId The id of the layer.
 * @returns Array of existing column names or error.
 */
export async function fetchLayerColumnNames(layerId: string): Promise<{
  columnNames: string[];
  error: PostgrestError | null;
}> {
  const supabase = getBrowserClient();
  const { data: existingColumns, error } = await supabase
    .from('layer_columns')
    .select('column_name')
    .eq('layer_id', layerId);

  if (error) {
    return { columnNames: [], error };
  }

  return {
    columnNames: existingColumns?.map(col => col.column_name) || [],
    error: null,
  };
}

/**
 * Adds a new column to a layer using the add_layer_column RPC.
 * @param layerId The id of the layer.
 * @param name The name of the new column.
 * @returns The result of the Supabase RPC operation.
 */
export async function addLayerColumn({
  layerId,
  name,
  isReadOnly = false,
  position,
}: AddLayerColumnArgs) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('add_layer_column', {
    in_layer_id: layerId,
    in_column_name: name,
    in_is_read_only: isReadOnly,
    in_position: position,
  });
  return { data, error };
}

/**
 * Adds new columns to a layer using the add_layer_columns RPC.
 * @param layerId The id of the layer.
 * @param names The names of the new columns.
 * @returns The result of the Supabase RPC operation.
 */
export async function addLayerColumns(
  layerId: string,
  names: string[],
  isReadOnly: boolean = false
) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('add_layer_columns', {
    in_layer_id: layerId,
    in_column_names: names,
    in_is_read_only: isReadOnly,
  });
  return { data, error };
}

/**
 * Adds a new row to a layer using the add_layer_row RPC.
 * @param layerId The id of the layer.
 * @param geometry Optional geometry — either [lng, lat] shorthand or full GeoJSON geometry.
 * @param properties The properties object for the row.
 * @returns The result of the Supabase RPC operation.
 */
export async function addLayerRow(
  layerId: string,
  geometry: [number, number] | GeoJSON.Geometry | null,
  properties: Record<string, any>,
  featureId?: string
) {
  const supabase = getBrowserClient();
  let geojson: GeoJSON.Geometry | null = null;
  if (geometry) {
    if (Array.isArray(geometry)) {
      // [lng, lat] shorthand → Point
      geojson = { type: 'Point', coordinates: [geometry[0], geometry[1]] };
    } else {
      geojson = geometry;
    }
  }

  const { data, error } = await supabase.rpc('add_layer_row', {
    in_layer_id: layerId,
    in_geometry: geojson,
    in_properties: properties,
    ...(featureId ? { in_feature_id: featureId } : {}),
  });
  return { data, error };
}

/**
 * Adds a new row to a layer, auto-creating any missing columns.
 * Use this instead of addLayerRow when the layer may not have all property columns yet
 * (e.g. site layers created before a new column was added to the required set).
 */
export async function addLayerRowEnsureColumns(
  layerId: string,
  geometry: [number, number] | GeoJSON.Geometry | null,
  properties: Record<string, any>,
  featureId?: string
) {
  const supabase = getBrowserClient();
  let geojson: GeoJSON.Geometry | null = null;
  if (geometry) {
    if (Array.isArray(geometry)) {
      geojson = { type: 'Point', coordinates: [geometry[0], geometry[1]] };
    } else {
      geojson = geometry;
    }
  }

  const { data, error } = await supabase.rpc('add_layer_row_ensure_columns', {
    in_layer_id: layerId,
    in_geometry: geojson,
    in_properties: properties,
    ...(featureId ? { in_feature_id: featureId } : {}),
  });
  return { data, error };
}

/**
 * Deletes a layer row by its id.
 * @param rowId The id of the row to delete.
 * @returns The result of the Supabase delete operation.
 */
export async function deleteLayerRow(rowId: number) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('delete_layer_row', {
    in_row_id: rowId,
  });
  return { data, error };
}

/**
 * Updates a layer row's properties by its id.
 * @param rowId The id of the row to update.
 * @param properties The properties object to update.
 * @returns The result of the Supabase RPC operation.
 */
export async function updateLayerRow(rowId: number, properties: Record<string, any>) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('update_layer_row_properties', {
    in_row_id: rowId,
    in_properties: properties,
  });
  return { data, error };
}

/**
 * Updates properties on an existing layer row, auto-creating any missing columns.
 * Use this instead of updateLayerRow when the column may not exist on the layer yet.
 */
export async function updateLayerRowEnsureColumns(rowId: number, properties: Record<string, any>) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('update_layer_row_properties_ensure_columns', {
    in_row_id: rowId,
    in_properties: properties,
  });
  return { data, error };
}

/**
 * Inserts a new layer row with the specified properties.
 * @param layerId The id of the layer to insert the row into.
 * @param properties The properties object for the new row.
 * @returns The result of the Supabase RPC operation.
 */
export async function insertLayerRow(layerId: string, properties: Record<string, any>) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('add_layer_row', {
    in_layer_id: layerId,
    in_geometry: null,
    in_properties: properties,
  });
  return { data, error };
}

/**
 * Updates a layer column's name by its name.
 * @param layerId The id of the layer.
 * @param columnName The name of the column to update.
 * @param newName The new name for the column.
 * @returns The result of the Supabase update operation.
 */
export async function updateLayerColumnName(layerId: string, columnName: string, newName: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.rpc('rename_layer_column', {
    in_layer_id: layerId,
    in_column_name: columnName,
    in_new_column_name: newName,
  });
  return { data, error };
}

/**
 * Deletes a layer column by its id.
 * @param columnId The id of the column to delete.
 * @returns The result of the Supabase delete operation.
 */
export async function deleteLayerColumn(layerId: string, columnName: string) {
  const supabase = getBrowserClient();

  const { data, error } = await supabase.rpc('delete_layer_column', {
    in_layer_id: layerId,
    in_column_name: columnName,
  });
  return { data, error };
}

export async function getLayerFolders(workspaceId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layer_folders')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('updated_at', { ascending: false });
  return { data, error };
}

export async function getLayerFolder(folderId: string, workspaceId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layer_folders')
    .select(
      `
      *,
      layers:layers(
      *,
      maps:map_layers(
        maps(id, title)
      ),
      updated_by_user:user_profiles!updated_by(
        id,
        display_name
      ))
    `
    )
    .eq('id', folderId)
    .eq('workspace_id', workspaceId)
    .single();
  return { data, error };
}

export async function updateFolder({
  id,
  name,
  workspaceId,
}: {
  id: string;
  name: string;
  workspaceId: string;
}) {
  const supabase = getBrowserClient();
  const updateData = {
    name: name.trim(),
  };
  const { data, error } = await supabase
    .from('layer_folders')
    .update(updateData)
    .eq('id', id)
    .eq('workspace_id', workspaceId)
    .select()
    .single();

  if (error || !data) {
    return { error, data: null };
  }

  const updatedFolder: LayerFolder = data;
  return { data: updatedFolder, error: null };
}

export async function deleteFolderById(id: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('layer_folders')
    .delete()
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function _updateLayerStatus(
  supabase: SupabaseClient,
  { layerId, status }: UpdateLayerStatusArgs
) {
  const { data, error } = await supabase
    .from('layers')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', layerId)
    .select();

  if (error) {
    console.error(`Failed to update layer ${layerId} status to ${status}:`, error);
  }

  return { data, error };
}

export async function _updateLayerGeometryType(
  supabase: SupabaseClient,
  { layerId, geometryType }: UpdateLayerGeometryTypeArgs
) {
  const { data, error } = await supabase
    .from('layers')
    .update({ geometry_type: geometryType, updated_at: new Date().toISOString() })
    .eq('id', layerId)
    .select();

  if (error) {
    console.error(`Failed to update layer ${layerId} geometryType to ${geometryType}:`, error);
  }

  return { data, error };
}
